import {
  CONFIRM_OUTCOME_MAX_WAIT_MS,
  CONFIRM_OUTCOME_POLL_INTERVAL_MS,
  CONFIRM_OUTCOME_REQUEST_TIMEOUT_MS,
} from "@/config";
import { withTimeout } from "@/utils/with-timeout";
import { getPaymentSessionStatus } from "./paymentService";
import type { PaymentSessionStatus } from "./types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Asks the backend how a confirm without an answer ended, so a donation that
 * went through reaches the thank-you page without a new card authorisation.
 * Only `in_progress` is worth waiting on: every other status is final for
 * this session. Returns null when the outcome is still unknown at the
 * deadline; the caller then falls back to the unknown-outcome banner.
 */
export async function awaitConfirmOutcome(
  sessionId: string,
): Promise<PaymentSessionStatus | null> {
  const deadline = Date.now() + CONFIRM_OUTCOME_MAX_WAIT_MS;

  while (Date.now() + CONFIRM_OUTCOME_POLL_INTERVAL_MS < deadline) {
    await wait(CONFIRM_OUTCOME_POLL_INTERVAL_MS);

    try {
      const outcome = await withTimeout(
        (signal) => getPaymentSessionStatus(sessionId, signal),
        Math.min(CONFIRM_OUTCOME_REQUEST_TIMEOUT_MS, deadline - Date.now()),
      );

      if (outcome.status !== "in_progress") return outcome;
    } catch {
      // The network that lost the confirm may still be down: ask again.
    }
  }

  return null;
}
